import { Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Switch, notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import {
  DEPARTMENT_OPTIONS,
  MISSION_PERIOD_OPTIONS,
  MISSION_STATUS_OPTIONS,
  STAR_GROUP_OPTIONS,
} from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { MissionAdmin, MissionPeriod } from 'pages/star-shared/types';

interface Props {
  open: boolean;
  mission: MissionAdmin | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Nhiệm vụ không cần duyệt = tự trả Sao khi nộp báo cáo: chỉ Admin tạo / sửa được (máy chủ trả
 * STAR_MISSION_AUTO_PAY_ADMIN_ONLY). Quản lý thì nhiệm vụ nào cũng phải qua duyệt.
 */
export const AUTO_PAY_ADMIN_ONLY_HINT = 'Chỉ Admin tạo được nhiệm vụ tự trả thưởng.';

// Tạo/sửa nhiệm vụ CLAIM (KPI tự báo cáo). Nhiệm vụ hệ thống không mở modal này.
const MissionModal = ({ open, mission, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Chưa biết vai trò (đang tải / lỗi) cũng coi như không phải Admin: luôn cần duyệt.
  const { isAdmin } = useCurrentRoles();
  // Giới hạn lần nộp (doc 10 §4.1): "/ngày" chỉ có nghĩa khi kỳ ≠ Ngày, "/tuần" chỉ khi kỳ = Tháng.
  const period = Form.useWatch('period', form) as MissionPeriod | undefined;
  const showMaxPerDay = period !== 'DAY';
  const showMaxPerWeek = period === 'MONTH';

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      code: mission?.code || '',
      title: mission?.title || '',
      description: mission?.description || '',
      rewardStars: mission?.rewardStars ?? 10,
      period: mission?.period || 'MONTH',
      target: mission?.target ?? 1,
      maxRewardsPerPeriod: mission?.maxRewardsPerPeriod ?? 1,
      maxPerDay: mission?.maxPerDay ?? null,
      maxPerWeek: mission?.maxPerWeek ?? null,
      needsApproval: mission?.needsApproval ?? true,
      starGroups: mission?.audience?.starGroups || [],
      departments: mission?.audience?.departments || [],
      startsAt: mission?.startsAt ? dayjs(mission.startsAt) : null,
      endsAt: mission?.endsAt ? dayjs(mission.endsAt) : null,
      status: mission?.status || 'DRAFT',
      sort: mission?.sort ?? 0,
    });
  }, [open, mission, form]);

  // Không phải Admin: công tắc "Cần duyệt báo cáo" luôn bật. Tách khỏi effect trên để vai trò tải
  // xong muộn không xoá những gì đã gõ.
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ needsApproval: isAdmin ? mission?.needsApproval ?? true : true });
  }, [open, isAdmin, mission, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    const payload: Record<string, any> = {
      title: values.title.trim(),
      description: values.description?.trim() || null,
      rewardStars: values.rewardStars,
      period: values.period,
      target: values.target,
      maxRewardsPerPeriod: values.maxRewardsPerPeriod,
      // Ô trống (undefined) phải gửi null rõ ràng để PATCH xoá giới hạn; ô đã ẩn theo kỳ vẫn còn
      // giá trị trong form (antd giữ) → ép null khi không áp dụng.
      maxPerDay: values.period === 'DAY' ? null : values.maxPerDay ?? null,
      maxPerWeek: values.period === 'MONTH' ? values.maxPerWeek ?? null : null,
      needsApproval: isAdmin ? !!values.needsApproval : true,
      audience: { starGroups: values.starGroups || [], departments: values.departments || [] },
      startsAt: values.startsAt ? dayjs(values.startsAt).startOf('day').toISOString() : null,
      endsAt: values.endsAt ? dayjs(values.endsAt).endOf('day').toISOString() : null,
      status: values.status,
      sort: values.sort ?? 0,
    };
    setLoading(true);
    try {
      if (mission) {
        await AxiosUtils.patch(`${API.starsMissions}/${mission.id}`, payload);
        notification.success({ message: 'Đã cập nhật nhiệm vụ.' });
      } else {
        await AxiosUtils.post(API.starsMissions, {
          code: String(values.code || '').trim().toUpperCase(),
          kind: 'CLAIM',
          metric: 'CLAIM',
          ...payload,
        });
        notification.success({ message: 'Đã tạo nhiệm vụ.' });
      }
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={mission ? 'Sửa nhiệm vụ' : 'Thêm nhiệm vụ tự báo cáo'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={760}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Mã nhiệm vụ"
              name="code"
              rules={[
                { required: true, message: 'Vui lòng nhập mã.' },
                { pattern: /^[A-Za-z0-9_]{3,40}$/, message: 'Chỉ chữ, số, gạch dưới (3–40 ký tự).' },
              ]}
              extra={mission ? 'Không đổi mã sau khi tạo.' : 'VD: MC_VIDEO_MONTH'}
            >
              <Input disabled={!!mission} placeholder="MC_VIDEO_MONTH" maxLength={40} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Tên nhiệm vụ"
              name="title"
              rules={[
                { required: true, whitespace: true, message: 'Vui lòng nhập tên.' },
                { max: 120, message: 'Tối đa 120 ký tự.' },
              ]}
            >
              <Input placeholder="VD: Đăng 4 video TikTok" maxLength={120} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Mô tả" name="description" rules={[{ max: 500, message: 'Tối đa 500 ký tự.' }]}>
          <Input.TextArea rows={2} maxLength={500} showCount placeholder="Cách hoàn thành, minh chứng cần nộp" />
        </Form.Item>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Thưởng (Sao)"
              name="rewardStars"
              rules={[{ required: true, type: 'integer', min: 1, message: 'Số nguyên ≥ 1.' }]}
            >
              <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Kỳ" name="period" rules={[{ required: true, message: 'Chọn kỳ.' }]}>
              <Select options={MISSION_PERIOD_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Mục tiêu (lần/kỳ)"
              name="target"
              rules={[{ required: true, type: 'integer', min: 1, message: 'Số nguyên ≥ 1.' }]}
            >
              <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Thưởng tối đa/kỳ"
              name="maxRewardsPerPeriod"
              rules={[{ required: true, type: 'integer', min: 1, message: 'Số nguyên ≥ 1.' }]}
            >
              <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Cần duyệt báo cáo"
              name="needsApproval"
              valuePropName="checked"
              extra={isAdmin ? undefined : AUTO_PAY_ADMIN_ONLY_HINT}
            >
              <Switch checkedChildren="Có" unCheckedChildren="Không" disabled={!isAdmin} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Trạng thái" name="status" rules={[{ required: true }]}>
              <Select options={MISSION_STATUS_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
        {showMaxPerDay && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tối đa lần nộp / ngày"
                name="maxPerDay"
                rules={[{ type: 'integer', min: 1, message: 'Số nguyên ≥ 1 hoặc để trống.' }]}
                extra="Để trống = không giới hạn. Đếm mọi lần nộp trong ngày, kể cả bị từ chối."
              >
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  min={1}
                  precision={0}
                  placeholder="Không giới hạn"
                />
              </Form.Item>
            </Col>
            {showMaxPerWeek && (
              <Col span={12}>
                <Form.Item
                  label="Tối đa lần nộp / tuần"
                  name="maxPerWeek"
                  rules={[{ type: 'integer', min: 1, message: 'Số nguyên ≥ 1 hoặc để trống.' }]}
                  extra="Để trống = không giới hạn. Tuần Thứ Hai – Chủ nhật."
                >
                  <InputNumber
                    size="large"
                    style={{ width: '100%' }}
                    min={1}
                    precision={0}
                    placeholder="Không giới hạn"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Nhóm Sao áp dụng" name="starGroups" extra="Để trống = mọi nhóm.">
              <Select mode="multiple" allowClear options={STAR_GROUP_OPTIONS} placeholder="Mọi nhóm" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Bộ phận áp dụng" name="departments" extra="Để trống = mọi bộ phận.">
              <Select mode="multiple" allowClear options={DEPARTMENT_OPTIONS} placeholder="Mọi bộ phận" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Bắt đầu" name="startsAt">
              <DatePicker format="DD/MM/YYYY" placeholder="Không giới hạn" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Kết thúc"
              name="endsAt"
              dependencies={['startsAt']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || !getFieldValue('startsAt') || !dayjs(value).isBefore(getFieldValue('startsAt'))
                      ? Promise.resolve()
                      : Promise.reject(new Error('Kết thúc phải sau bắt đầu.')),
                }),
              ]}
            >
              <DatePicker format="DD/MM/YYYY" placeholder="Không giới hạn" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Thứ tự" name="sort">
              <InputNumber size="large" style={{ width: '100%' }} precision={0} />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={mission ? 'Lưu' : 'Tạo'}
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default MissionModal;
